import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { ChevronDown } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { formatCurrency } from "../../utils/formatters";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { currencyPattern } from "@acto/taskpane/core/utils/currencyPattern";
import { usePopover } from "../primitives/usePopover";
import { FilterPillTrigger, SelectPopover } from "../primitives/selectParts";

const ActivePillTrigger = styled(FilterPillTrigger)(({ theme }) => ({
  paddingLeft: theme.managementView.spacing[8],
  color: theme.managementView.colors.neutral[90],
}));

const CurrencyIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  flexShrink: 0,
  borderRadius: "50%",
  backgroundColor: theme.managementView.colors.success[4],
  color: theme.managementView.colors.success[100],
  fontSize: "13px",
  fontWeight: theme.managementView.font.weight[600],
  lineHeight: 1,
}));

const TriggerLabelText = styled("span")(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Popover = styled(SelectPopover)(({ theme }) => ({
  minWidth: "260px",
  padding: theme.managementView.spacing[16],
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

const FieldRow = styled("label")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: "13px",
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
}));

const InputWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  height: "40px",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[32],
  paddingLeft: theme.managementView.spacing[18],
  paddingRight: theme.managementView.spacing[12],
  backgroundColor: "transparent",
}));

const CurrencyAdornment = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[56],
  marginRight: theme.managementView.spacing[6],
}));

const NumberInput = styled("input")(({ theme }) => ({
  flex: "1 1 auto",
  border: "none",
  outline: "none",
  background: "transparent",
  height: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[90],
  minWidth: 0,
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "&[type=number]": {
    MozAppearance: "textfield",
  },
}));

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  marginTop: theme.managementView.spacing[4],
}));

const TextButton = styled("button")(({ theme }) => ({
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: "13px",
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[16]}`,
  borderRadius: theme.managementView.radius[16],
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

const PrimaryButton = styled("button")(({ theme }) => ({
  background: theme.managementView.colors.neutral[90],
  color: theme.managementView.colors.neutral[0],
  border: "none",
  cursor: "pointer",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: "13px",
  fontWeight: theme.managementView.font.weight[500],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[16]}`,
  borderRadius: theme.managementView.radius[32],
  "&:hover:not(:disabled)": {
    opacity: 0.9,
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

export interface RevenueRangeValue {
  min: number | null;
  max: number | null;
}

interface RevenueRangeFilterProps {
  value: RevenueRangeValue;
  onChange: (value: RevenueRangeValue) => void;
  disabled?: boolean;
}

const parseNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/,/g, ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toDraft = (n: number | null): string => (n != null ? String(n) : "");

export const RevenueRangeFilter = ({ value, onChange, disabled }: RevenueRangeFilterProps) => {
  const t = useTranslations("customers.filters");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();

  const [minDraft, setMinDraft] = useState<string>(toDraft(value.min));
  const [maxDraft, setMaxDraft] = useState<string>(toDraft(value.max));

  // Reset drafts to committed value whenever the popover opens or the external value changes.
  useEffect(() => {
    setMinDraft(toDraft(value.min));
    setMaxDraft(toDraft(value.max));
  }, [value.min, value.max, isOpen]);

  const { symbol } = currencyPattern(locale, currency);
  const formatValue = useCallback((n: number) => formatCurrency(n, locale, currency), [locale, currency]);

  const triggerLabel = useMemo(() => {
    const { min, max } = value;
    const placeholder = t("revenuePlaceholder");
    if (min == null && max == null) return placeholder;
    if (min != null && max != null) return `${formatValue(min)} – ${formatValue(max)}`;
    if (min != null) return `${formatValue(min)}+`;
    if (max != null) return `≤ ${formatValue(max)}`;
    return placeholder;
  }, [value, formatValue, t]);

  const handleApply = () => {
    const parsedMin = parseNumber(minDraft);
    const parsedMax = parseNumber(maxDraft);
    if (parsedMin !== value.min || parsedMax !== value.max) {
      onChange({ min: parsedMin, max: parsedMax });
    }
    close();
  };

  const handleClear = () => {
    setMinDraft("");
    setMaxDraft("");
    if (value.min != null || value.max != null) {
      onChange({ min: null, max: null });
    }
    close();
  };

  const hasAnyValue = minDraft !== "" || maxDraft !== "";
  const draftsMatchCommitted = parseNumber(minDraft) === value.min && parseNumber(maxDraft) === value.max;

  const hasCommittedValue = value.min != null || value.max != null;

  return (
    <>
      {hasCommittedValue ? (
        <ActivePillTrigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
          <CurrencyIcon aria-hidden="true">{symbol}</CurrencyIcon>
          <TriggerLabelText>{triggerLabel}</TriggerLabelText>
          <ChevronDown />
        </ActivePillTrigger>
      ) : (
        <FilterPillTrigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
          {triggerLabel}
          <ChevronDown />
        </FilterPillTrigger>
      )}
      {isOpen &&
        createPortal(
          <Popover
            ref={popoverRef}
            style={position}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
          >
            <FieldRow>
              {t("revenueMin")}
              <InputWrapper>
                <CurrencyAdornment>{symbol}</CurrencyAdornment>
                <NumberInput
                  type="number"
                  inputMode="decimal"
                  value={minDraft}
                  onChange={(e) => setMinDraft(e.target.value)}
                />
              </InputWrapper>
            </FieldRow>
            <FieldRow>
              {t("revenueMax")}
              <InputWrapper>
                <CurrencyAdornment>{symbol}</CurrencyAdornment>
                <NumberInput
                  type="number"
                  inputMode="decimal"
                  value={maxDraft}
                  onChange={(e) => setMaxDraft(e.target.value)}
                />
              </InputWrapper>
            </FieldRow>
            <Footer>
              <TextButton type="button" onClick={handleClear} disabled={!hasAnyValue}>
                {t("clear")}
              </TextButton>
              <PrimaryButton type="button" onClick={handleApply} disabled={draftsMatchCommitted}>
                {t("apply")}
              </PrimaryButton>
            </Footer>
          </Popover>,
          document.body,
        )}
    </>
  );
};
